import RenderArticleBlocks from "@blocks/RenderArticleBlocks/RenderArticleBlocks";
import OutbrainMainContainer, {
  AdsSettings,
} from "@container/OutbrainMainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { formatDate } from "@helper/useDateFormat";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import WordPuzzle from "@modules/WordPuzzle/WordPuzzle";
import ArticleContentCard from "@pages/Article/ArticleContentCard";
import ArticleInfiniteScroll from "@pages/Article/ArticleInfiniteScroll";
import ArticleMetaData from "@pages/Article/ArticleMetaData";
import ErrorPage from "@pages/ErrorPage";
import NotFound from "@pages/NotFound";
import {
  CustomContext,
  TRouteWithoutRedirect,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ArticleAdPageTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { ArticlePayloadResponseType } from "@typings/PagePayloadType";
import { ReactElement, useState } from "react";

export default function ArticlePage(): ReactElement {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<ArticlePayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  const [currentArticle, setCurrentArticle] = useState<ProcessedArticleData>();
  const adsSettings: AdsSettings = useGlobalAdSetting();
  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const { gsChannels } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const { isScreenLG } = useWindowSize();

  if (
    dataLoaderResponse.type === ResponseType.CLIENT_ERROR ||
    dataLoaderResponse.type === ResponseType.SERVER_ERROR
  ) {
    return dataLoaderResponse.type === ResponseType.CLIENT_ERROR ? (
      <NotFound />
    ) : (
      <ErrorPage message={dataLoaderResponse.payload ?? ""} />
    );
  }
  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

  const mainArticle = dataLoaderResponse.payload.article;
  const sectionName = mainArticle.sectionName;
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri || "";
  // Check Cue preview
  const isCuePreview = dataLoaderResponse.payload.isPreview
    ? dataLoaderResponse.payload.isPreview
    : false;

  const articlePageTarget: ArticleAdPageTargetingType[] = [
    { key: "page_number", value: "1" },
    { key: "tmarticleid", value: mainArticle.id },
    {
      key: "tmtags",
      value: mainArticle.tags?.map((tags) => tags.name).join(", ") || "",
    },
  ];

  const handleNewArticle = (
    newArticle: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => {
    setCurrentArticle(newArticle);
    if (viewPortChange) {
      setTimeout(() => {
        TmCustomGaEvent({
          event: "virtualPV",
          articleid: newArticle.id,
          byline: newArticle.authors
            ? newArticle.authors.map((author) => author.name).join(", ")
            : "",
          chapter1: "",
          contenttype: "article",
          cue_articleid: newArticle.id,
          keyword: newArticle.tags
            ? newArticle.tags.map((tags) => tags.name).join(", ")
            : "",
          landingPage: origin + mainArticle.urlPath,
          level2: newArticle.sectionName,
          nextNodeTitle: newArticle.title,
          nextNodeUrl: origin + newArticle.urlPath,
          pubdate: formatDate(newArticle.dateCreated, "yyyy-MM-dd HH:mm:ss"),
          title: mainArticle.title,
          pagination: pageNumber,
        });
      }, 300);
    }
  };

  const { gaData, metaTags } = ArticleMetaData({
    article: currentArticle || mainArticle,
    isMainArticle: currentArticle ? false : true,
    gsChannels:
      currentArticle && currentArticle.gsChannels
        ? currentArticle.gsChannels
        : gsChannels,
  });

  return (
    <OutbrainMainContainer
      metaTagsProps={metaTags}
      gaData={gaData}
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adsSettings}
      gsChannels={gsChannels}
      isArticlePage={true}
    >
      <div id={mainArticle.id} className="article-card">
        <WrapperBlock className="hover:text flex flex-col lg:flex-row justify-between">
          <ArticleContentCard
            articleData={mainArticle}
            pageNumber={0}
            isInfiniteScrollArticle={false}
            hasTaboola={adsSettings.isTaboolaEnabled}
            gsChannels={gsChannels}
            isCuePreview={isCuePreview}
          />
          {isScreenLG ? (
            <SideBlock
              sectionName={sectionName}
              pageAdTargetValue={pageAdTargetValue}
              articlePageTarget={articlePageTarget}
              gsChannels={gsChannels}
              promoteEpaper={true}
              epaperCoverImageUrl={epaperCoverImageUrl}
            />
          ) : (
            <>
              {/* Mobile infinite scroll view */}
              {/* Render first block on default article */}
              <RenderArticleBlocks
                index={0}
                pageNumber={0}
                pageUrl={mainArticle.urlPath ? mainArticle.urlPath : ""}
                hasTaboola={adsSettings.isTaboolaEnabled}
                articleData={mainArticle}
                pageAdTargetValue={pageAdTargetValue}
                gsChannels={gsChannels}
              />
              <div className="border-y border-solid border-pink-300 mt-5">
                <AdsImu2
                  sectionName={sectionName}
                  pageAdTargetType={pageAdTargetValue}
                  rootClassName="mt-5"
                  articlePageTarget={articlePageTarget}
                  gsChannels={gsChannels}
                />
              </div>
            </>
          )}
        </WrapperBlock>
      </div>

      <ArticleInfiniteScroll
        mainArticle={mainArticle}
        pageAdTargetValue={pageAdTargetValue}
        onNewArticle={handleNewArticle}
        hasTaboola={adsSettings.isTaboolaEnabled}
        gsChannels={gsChannels}
        isCuePreview={isCuePreview}
      />
      <WordPuzzle isCatfishEnabled={adsSettings.isCatfishEnabled} />
    </OutbrainMainContainer>
  );
}
